/** Operator Desk → Approvals deep-link query shape for email / Telegram / push. */

import { parseOperatorDeskWorkItemId } from './operatorDeskWork';

export const OPERATOR_DESK_APPROVALS_QUERY = {
  desk: 'desk',
  tab: 'desk_tab',
  work: 'work',
} as const;

export const OPERATOR_DESK_APPROVALS_TAB = 'approvals';

export type OperatorDeskApprovalsDeepLink = {
  workId: string;
  tab: typeof OPERATOR_DESK_APPROVALS_TAB;
};

const DEFAULT_ORIGIN = 'https://ziawellnav.com';

export function isOperatorDeskApprovalsWorkId(value: unknown): value is string {
  const id = String(value || '').trim();
  if (!id || id.length > 120) return false;
  return Boolean(parseOperatorDeskWorkItemId(id));
}

/** Parse `?desk=1&desk_tab=approvals&work=haul:…` (also accepts desk_tab omitted when work is set). */
export function parseOperatorDeskApprovalsDeepLink(
  search: string | URLSearchParams | null | undefined,
): OperatorDeskApprovalsDeepLink | null {
  let params: URLSearchParams;
  try {
    if (search instanceof URLSearchParams) params = search;
    else {
      const raw = String(search || '');
      params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
    }
  } catch {
    return null;
  }
  const desk = String(params.get(OPERATOR_DESK_APPROVALS_QUERY.desk) || '').trim();
  const workId = String(params.get(OPERATOR_DESK_APPROVALS_QUERY.work) || '').trim();
  const tabRaw = String(params.get(OPERATOR_DESK_APPROVALS_QUERY.tab) || '').trim().toLowerCase();
  if (desk !== '1' && desk.toLowerCase() !== 'true') return null;
  if (!isOperatorDeskApprovalsWorkId(workId)) return null;
  if (tabRaw && tabRaw !== OPERATOR_DESK_APPROVALS_TAB) return null;
  return { workId, tab: OPERATOR_DESK_APPROVALS_TAB };
}

export function buildOperatorDeskApprovalsPath(workId: string): string | null {
  if (!isOperatorDeskApprovalsWorkId(workId)) return null;
  const params = new URLSearchParams();
  params.set(OPERATOR_DESK_APPROVALS_QUERY.desk, '1');
  params.set(OPERATOR_DESK_APPROVALS_QUERY.tab, OPERATOR_DESK_APPROVALS_TAB);
  params.set(OPERATOR_DESK_APPROVALS_QUERY.work, workId);
  return `/?${params.toString()}`;
}

export function buildOperatorDeskApprovalsUrl(
  env: { PUBLIC_APP_URL?: string; APP_BASE_URL?: string } | null | undefined,
  workId: string,
  requestUrl?: string,
): string | null {
  const path = buildOperatorDeskApprovalsPath(workId);
  if (!path) return null;
  let base = String(env?.PUBLIC_APP_URL || env?.APP_BASE_URL || '').replace(/\/$/, '');
  if (!base && requestUrl) {
    try {
      base = new URL(requestUrl).origin;
    } catch {
      base = '';
    }
  }
  if (!base) base = DEFAULT_ORIGIN;
  return `${base}${path}`;
}

/** Session key VoiceNavigation / LandingPage use to open Approvals after sign-in. */
export const ZIA_OPEN_OPERATOR_DESK_APPROVALS_KEY = 'zia_open_operator_desk_approvals';
